import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Typography,
  Box,
  Alert,
  Paper,
  Avatar,
} from "@mui/material";
import {
  Person as PersonIcon,
} from "@mui/icons-material";
import { fetchUserCompaniesRequest } from "../store/slices/database/userCompaniesSlice";
import CustomPagination from "../components/common/CustomPagination";
import DataContainer from "../components/common/DataContainer/DataContainer";
import { useAuth } from "../hooks/useAuth";
import { useNavigate } from "react-router-dom";
import { setCompanyData, setCompany } from '../store/slices/database/selectedCompanySlice';
import { fetchUserAccessDataRequest } from '../store/slices/database/userAccessDataSlice';
import { setProtectedMenuData } from '../store/slices/database/menuConfigSlice';
import Loader from "../components/common/custom-component/Loader";
import LoadingBackdrop from "../components/common/custom-component/LoadingBackdrop";
import { logger } from "helpers/logger";
import { fetchCompanyRolesThunk } from "../store/thunks/mdsThunks";
import { useTableColumns } from "../hooks/useTableColumns";

export default function UserCompaniesPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const keycloakAuth = useAuth();

  const { data: userCompaniesData = [], loading: companiesLoading, error: companiesError } = useSelector((state) => state.userCompanies?.data || {});

  const userMenusFetched = useSelector((state) => state.userAccessData.data?.data?.userMenus);
  const userMenusCache = useSelector((state) => state.menuConfig.data?.protected);
  const userMenus = userMenusFetched ?? userMenusCache;

  const selectedCompany = useSelector((state) => state.selectedCompany.data?.companyData);

  const prevCompanyIdRef = useRef(selectedCompany?.companyId);
  const prevMenuRef = useRef(userMenus);
  const autoSelectCompanyRef = useRef(false);

  const [isLoadingBackdrop, setIsLoadingBackdrop] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  const recordsPerPage = 10;
  const totalPages = Math.ceil(userCompaniesData.length / recordsPerPage);

  const user = keycloakAuth.user;
  const userId = user?.id;

  // Derived state
  const isBackgroundRefreshing = companiesLoading && userCompaniesData.length > 0;

  const fetchCompanyRoles = useCallback(
    async (companyId) => {
      try {

        const companyRolesResponse = await dispatch(fetchCompanyRolesThunk({ companyId }));

        if (fetchCompanyRolesThunk.rejected.match(companyRolesResponse)) {
          logger.error("Error fetching company roles:", companyRolesResponse.payload?.data);
          return null;
        }

        const companyRoles = companyRolesResponse.payload?.data;
        //logger.info(companyRoles)
        return companyRoles;
      } catch (error) {
        logger.error("Error in fetchCompanyRoles:", error);
        return null;
      }
    },
    [dispatch]
  );



  /* -------------------------------- HANDLERS -------------------------------- */

  const handleSelectClick = useCallback(async (company) => {
    if (company?.companyId && company.companyId !== prevCompanyIdRef.current) {
      setIsLoadingBackdrop(true);
      const companyRolesRecs = await fetchCompanyRoles(company.companyId)
      logger.info("companyRolesRecs roles:", companyRolesRecs);
      const formattedRoles = companyRolesRecs.map(role => ({
        value: role.roleName,
        label: role.roleName
      }));
      logger.info("Formatted roles:", formattedRoles);
      dispatch(setCompany({ companyData: company, companyRoles: formattedRoles }));
    } else {
      const hasDashboards = userMenus?.some(route => route.to === "/dashboards");
      const hasUserTasks = userMenus?.some(route => route.to === "/userTasks");
      navigate(hasDashboards ? "/dashboards" : hasUserTasks ? "/userTasks" : "/workflowHistory");
    }
  }, [dispatch, navigate, fetchCompanyRoles, userMenus]);

  /* -------------------------------- EFFECTS -------------------------------- */

  useEffect(() => {
    if (keycloakAuth.isAuthenticated && userId) {
      dispatch(fetchUserCompaniesRequest(userId));
    }
  }, [dispatch, keycloakAuth.isAuthenticated, userId]);

  useEffect(() => {
    if (!selectedCompany?.companyId || !userId) return;

    if (selectedCompany.companyId !== prevCompanyIdRef.current) {
      prevCompanyIdRef.current = selectedCompany.companyId;
      dispatch(fetchUserAccessDataRequest({ userId, companyId: selectedCompany.companyId }));
    }
  }, [dispatch, userId, selectedCompany?.companyId]);

  useEffect(() => {
    if (!autoSelectCompanyRef.current && userCompaniesData.length === 1) {
      autoSelectCompanyRef.current = true;
      handleSelectClick(userCompaniesData[0]);
    }
  }, [userCompaniesData, handleSelectClick]);

  useEffect(() => {
    if (userMenus && userMenus !== prevMenuRef.current) {
      prevMenuRef.current = userMenus;
      dispatch(setProtectedMenuData(userMenus));
      setIsLoadingBackdrop(false);
      const hasDashboards = userMenus?.some(route => route.to === "/dashboards");
      const hasUserTasks = userMenus?.some(route => route.to === "/userTasks");
      navigate(hasDashboards ? "/dashboards" : hasUserTasks ? "/userTasks" : "/workflowHistory");
    }
  }, [userMenus, dispatch, navigate]);

  /* -------------------------------- DYNAMIC COLUMNS -------------------------------- */

  // Memoize actions and context to prevent unnecessary recalculations in useTableColumns
  const actions = useMemo(() => ({
    onSelect: handleSelectClick,
  }), [handleSelectClick]);

  const context = useMemo(() => ({
    selectedCompanyId: selectedCompany?.companyId,
  }), [selectedCompany?.companyId]);

  // Use the dynamic columns hook - fetches config from Supabase based on domain
  const { columns } = useTableColumns('UserCompanies', {
    actions,
    context,
  });

  /* -------------------------------- RENDER -------------------------------- */

  if (companiesLoading && userCompaniesData.length === 0) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
        <Loader variant="circular" message="Loading your companies..." size="small" />
      </Box>
    );
  }

  if (companiesError) {
    return <Alert severity="error">{companiesError}</Alert>;
  }

  return (
    <Box>
      <Paper elevation={1} sx={{ p: 2, mb: 3, bgcolor: "primary.main" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Avatar sx={{ bgcolor: "primary.main" }}>
            <PersonIcon />
          </Avatar>
          <Typography variant="h6" sx={{ color: "primary.contrastText" }}>
            {user?.firstName || user?.username}'s Companies
          </Typography>
        </Box>
      </Paper>


      <Box sx={{ padding: "1%" }}>
        {isBackgroundRefreshing && (
          <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
            <Loader variant="circular" message="Loading..." size="small" />
          </Box>
        )}
        <DataContainer
          data={userCompaniesData}
          columns={columns}
          showRowNumbers
          stickyHeader
          rowKey="companyId"
          maxHeight={600}
        />
      </Box>

      {totalPages > 1 && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
          <CustomPagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={(e, value) => setCurrentPage(value)}
          />
        </Box>
      )}

      <LoadingBackdrop
        open={isLoadingBackdrop}
        message="Loading data, please wait..."
        variant="bars"
        color="greenGradient"
      />
    </Box>
  );
}
